import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import Layout from './Layout/layout.tsx';
import AuthenticatedRoute from './Auth/AuthenticatedRoute.tsx';
import WildCardRoute from './Components/WildCardRoute.tsx';
import { useBrokenLinkDialog } from './Context/BrokenLinkDialogContext.tsx';
import { useUserContext } from './Context/UserContext.tsx';
import SessionService from './Services/SessionService.tsx';
import BrokenLinkDialog from './Components/BrokenLinkDialog.tsx';
import { setAdminAccessDeniedRedirect } from './Services/AdminAccessDeniedHelper.tsx';

const Dashboard = lazy(() => import("./Pages/Dashboard.tsx"));
const EvaluationPlan = lazy(() => import("./Pages/EvaluationPlan.tsx"));
const MetricAverage = lazy(() => import("./Pages/MetricAverage.tsx"));
const ApexAdmin = lazy(() => import("./Pages/apex-admin.tsx"));
const GrowthLanding = lazy(() => import("./Pages/GrowthLanding.tsx"));

const routes = [
  { path: "/", component: Dashboard },
  { path: "/evaluationPlan/:shareholderId/:currentFiscalYearId", component: EvaluationPlan },
  { path: "/evaluationPlan/:shareholderId/:currentFiscalYearId/:isselfplan", component: EvaluationPlan },
  { path: "/evaluationPlan", component: EvaluationPlan },
];

const RoleBasedRoute = ({ allowedRoles, children }: any) => {

  const { role } = useUserContext();

  if (!role) return <Navigate to="/" replace />;

  const normalizedRole = role?.toLowerCase();

  if (!allowedRoles.map((r: any) => r.toLowerCase()).includes(normalizedRole)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AdminRoute = ({ children }: any) => {
  const { isAdmin } = useUserContext();

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const GrowthRoute = ({ children }: any) => {
  const { isGrowthTeam } = useUserContext();

  if (!isGrowthTeam) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function BrokenLinkDialogHandler() {

  const { visible, hideDialog, setThroughWCvisible } = useBrokenLinkDialog();
  const navigate = useNavigate();
  const { role } = useUserContext();  
  const { shareholderId } = useParams<{ shareholderId: any }>();
  const { currentFiscalYearId } = useParams<{ currentFiscalYearId: any }>();

  const handleRedirect = () => {
    hideDialog();    
    setThroughWCvisible(false); 
    if (role?.toLocaleLowerCase() === 'leader' || role?.toLocaleLowerCase() === 'coleader') {
      SessionService.setNavigateTo('LeaderLandingPage');
      navigate('/');
    } 
    else if (role?.toLocaleLowerCase() === 'shareholder') {
      navigate(`/evaluationPlan`);
    } 
    else {
      navigate('/'); 
    }
  }

  return (
    visible &&
    <BrokenLinkDialog   
      shareholderId={shareholderId}
      currentFiscalYearId={currentFiscalYearId}    
      visible={visible}
      onRedirect={handleRedirect}
    />
  );
}

/* Registers the redirect the admin-panel service calls when an admin endpoint
   answers 403. Lives here because it needs useNavigate, and the providers in
   App.tsx sit outside the Router. */
function AdminAccessDeniedHandler() {

  const navigate = useNavigate();
  const { clearAdminAccess } = useUserContext();

  useEffect(() => {
    setAdminAccessDeniedRedirect(() => {
      /* Drop admin rights first: that hides the sidebar's Apex Admin entry and
         arms AdminRoute, so the user cannot walk straight back in. */
      clearAdminAccess();
      SessionService.setNavigateTo('LeaderLandingPage');
      navigate('/', { replace: true });
    });
  }, [navigate, clearAdminAccess]);

  return null;
}

export const AppRoutes = () => {
  return (
    <>
    <AdminAccessDeniedHandler />
    <Routes>
      <Route
        path="/"
        element={
          <AuthenticatedRoute>
            <Layout />
          </AuthenticatedRoute>
        }
      >
        {routes.map(({ path, component: Component }, index) => (
          <Route
            key={`${index}-${Component.name}`}
            path={path}
            element={
              <AuthenticatedRoute>
                <Suspense >
                  {/* <div className='main-contain' style={{ overflow: 'auto' }}> */}
                    <Component />
                  {/* </div> */}
                  <BrokenLinkDialogHandler />
                </Suspense>
              </AuthenticatedRoute>
            }
          />
        ))}
        <Route
          path="/apex-admin"
          element={
            <AuthenticatedRoute>
              <AdminRoute>
                <Suspense>
                  <ApexAdmin />
                  <BrokenLinkDialogHandler />
                </Suspense>
              </AdminRoute>
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/growth"
          element={
            <AuthenticatedRoute>
              <GrowthRoute>
                <Suspense>
                  <GrowthLanding />
                  <BrokenLinkDialogHandler />
                </Suspense>
              </GrowthRoute>
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/growth/:growthFiscalYearId"
          element={
            <AuthenticatedRoute>
              <GrowthRoute>
                <Suspense>
                  <GrowthLanding />
                  <BrokenLinkDialogHandler />
                </Suspense>
              </GrowthRoute>
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/growth/reportee/:apexUserId/:fiscalYearId"
          element={
            <AuthenticatedRoute>
              <RoleBasedRoute allowedRoles={['leader', 'CoLeader', 'Leader & CoLeader']}>
                <Suspense>
                  <GrowthLanding />
                  <BrokenLinkDialogHandler />
                </Suspense>
              </RoleBasedRoute>
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/averages"
          element={
            <AuthenticatedRoute>
              <RoleBasedRoute allowedRoles={['leader', 'CoLeader', 'Leader & CoLeader']}>
                <Suspense>
                  <MetricAverage />
                  <BrokenLinkDialogHandler />
                </Suspense>
              </RoleBasedRoute>
            </AuthenticatedRoute>
          }
        />
        <Route path="/favorites" element={<Navigate to="/" replace />} />
        <Route path="*" element={<WildCardRoute />} />
      </Route>
    </Routes>
    </>
  );
};
 


